import { cacheLife } from "next/cache"
import { wordPressApi } from "./api"

export interface Page {
  page_data: {
    id: number
    title: string
    slug: string
    acf_fields: AcfFields
  }
}

export interface AcfFields {
  pageSections: PageSection[]
  pagesections?: PageSection[]
}

export interface PageSection {
  acf_fc_layout: string
  slides?: Slide[]
  featuredProductsGroup?: FeaturedProductsGroup[]
  featuredproductsgroup?: FeaturedProductsGroup[]
}

export interface Slide {
  image: Image
  imageMobile: ImageMobile
  link: Link
}

export interface Image {
  ID: number
  id: number
  title: string
  filename: string
  filesize: number
  url: string
  link: string
  alt: string
  author: string
  description: string
  caption: string
  name: string
  status: string
  uploaded_to: number
  date: string
  modified: string
  menu_order: number
  mime_type: string
  type: string
  subtype: string
  icon: string
  width: number
  height: number
  sizes: Sizes
}

export interface Sizes {
  thumbnail: string
  "thumbnail-width": number
  "thumbnail-height": number
  medium: string
  "medium-width": number
  "medium-height": number
  medium_large: string
  "medium_large-width": number
  "medium_large-height": number
  large: string
  "large-width": number
  "large-height": number
  "1536x1536": string
  "1536x1536-width": number
  "1536x1536-height": number
  "2048x2048": string
  "2048x2048-width": number
  "2048x2048-height": number
  "martfury-blog-grid": string
  "martfury-blog-grid-width": number
  "martfury-blog-grid-height": number
  "martfury-blog-list": string
  "martfury-blog-list-width": number
  "martfury-blog-list-height": number
  "martfury-blog-masonry": string
  "martfury-blog-masonry-width": number
  "martfury-blog-masonry-height": number
  woocommerce_thumbnail: string
  "woocommerce_thumbnail-width": number
  "woocommerce_thumbnail-height": number
  woocommerce_single: string
  "woocommerce_single-width": number
  "woocommerce_single-height": number
  woocommerce_gallery_thumbnail: string
  "woocommerce_gallery_thumbnail-width": number
  "woocommerce_gallery_thumbnail-height": number
}

export interface ImageMobile {
  ID: number
  id: number
  title: string
  filename: string
  filesize: number
  url: string
  link: string
  alt: string
  author: string
  description: string
  caption: string
  name: string
  status: string
  uploaded_to: number
  date: string
  modified: string
  menu_order: number
  mime_type: string
  type: string
  subtype: string
  icon: string
  width: number
  height: number
  sizes: Sizes2
}

export interface Sizes2 {
  thumbnail: string
  "thumbnail-width": number
  "thumbnail-height": number
  medium: string
  "medium-width": number
  "medium-height": number
  medium_large: string
  "medium_large-width": number
  "medium_large-height": number
  large: string
  "large-width": number
  "large-height": number
  "1536x1536": string
  "1536x1536-width": number
  "1536x1536-height": number
  "2048x2048": string
  "2048x2048-width": number
  "2048x2048-height": number
  "martfury-blog-grid": string
  "martfury-blog-grid-width": number
  "martfury-blog-grid-height": number
  "martfury-blog-list": string
  "martfury-blog-list-width": number
  "martfury-blog-list-height": number
  "martfury-blog-masonry": string
  "martfury-blog-masonry-width": number
  "martfury-blog-masonry-height": number
  woocommerce_thumbnail: string
  "woocommerce_thumbnail-width": number
  "woocommerce_thumbnail-height": number
  woocommerce_single: string
  "woocommerce_single-width": number
  "woocommerce_single-height": number
  woocommerce_gallery_thumbnail: string
  "woocommerce_gallery_thumbnail-width": number
  "woocommerce_gallery_thumbnail-height": number
}

export interface Link {
  title: string
  url: string
  target: string
}

export interface FeaturedProductsGroup {
  mainTitleMainProducts: string
  productCategory: ProductCategory[]
  orderBy: string
  maintitlemainproducts?: string
  productcategory?: ProductCategory[]
  orderby?: string
}

const normalizeFeaturedGroup = (
  group: FeaturedProductsGroup
): FeaturedProductsGroup => {
  return {
    ...group,
    mainTitleMainProducts:
      group.mainTitleMainProducts ?? group.maintitlemainproducts ?? "",
    productCategory: Array.isArray(group.productCategory)
      ? group.productCategory
      : Array.isArray(group.productcategory)
        ? group.productcategory
        : [],
    orderBy: group.orderBy ?? group.orderby ?? "",
  }
}

export interface ProductCategory {
  term_id: number
  name: string
  slug: string
  term_group: number
  term_taxonomy_id: number
  taxonomy: string
  description: string
  parent: number
  count: number
  filter: string
}

export const getPageBySlug = async ({
  slug = "pagina-principal",
}: {
  slug: string
}) => {
  "use cache"

  cacheLife("days")

  const { data } = await wordPressApi.get<Page>(
    `/triibo/v1/page-by-slug/${slug}`
  )

  const pageSections = data?.page_data?.acf_fields?.pageSections
  const legacyPageSections = data?.page_data?.acf_fields?.pagesections

  if (!Array.isArray(pageSections) && Array.isArray(legacyPageSections)) {
    data.page_data.acf_fields.pageSections = legacyPageSections
  }

  const normalizedSections = data?.page_data?.acf_fields?.pageSections

  if (Array.isArray(normalizedSections)) {
    data.page_data.acf_fields.pageSections = normalizedSections.map(
      (section) => {
        if (section?.acf_fc_layout === "featuredProducts") {
          const legacyGroup = section.featuredproductsgroup
          const currentGroups = section.featuredProductsGroup

          if (!Array.isArray(currentGroups) && Array.isArray(legacyGroup)) {
            section.featuredProductsGroup = legacyGroup.map(
              normalizeFeaturedGroup
            )
          }

          if (Array.isArray(currentGroups)) {
            section.featuredProductsGroup = currentGroups.map(
              normalizeFeaturedGroup
            )
          }
        }

        return section
      }
    )
  }

  return data
}
